import { useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Money } from "../../../../components/Money";
import { getProfitMarginMetrics } from "../../../../api/ProfitMarginService";
import { useSeller } from "../../../../contexts/SellerContext";
import { useLangApp } from "../../../../contexts/LangContext";
import { dateToMomentFormat } from "../../../../utils/DateFormat";
import moment from "moment";
import {
  ProfitMarginContainer,
  ProfitMarginHeader,
  ProfitMarginTitle,
  MonthSelect,
  MetricsRow,
  MetricCard,
  MetricLabel,
  MetricValue,
  MarginPercentage,
} from "./styles";

const getMonthOptions = (lang) => {
  const months = [];
  for (let i = 0; i < 12; i++) {
    const date = moment().month(i);
    const label = date.locale(lang === "es" ? "es" : "en").format("MMMM");
    months.push({
      value: i,
      label: label.charAt(0).toUpperCase() + label.slice(1),
    });
  }
  return months;
};

export const ProfitMargin = ({ date }) => {
  const { sellerData } = useSeller();
  const { currentLang } = useLangApp();
  const intl = useIntl();
  const [data, setData] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(moment().month());
  const monthOptions = getMonthOptions(currentLang.name);

  useEffect(() => {
    if (date) {
      setSelectedMonth(moment(date, "YYYY-MM-DD").month());
    }
  }, [date]);

  useEffect(() => {
    if (sellerData?.user_id && sellerData?.idr) {
      loadProfitMargin();
    }
  }, [selectedMonth, sellerData]);

  const loadProfitMargin = async () => {
    try {
      const chosenDate = moment()
        .month(selectedMonth)
        .date(1)
        .format("YYYY-MM-DD");
      const response = await getProfitMarginMetrics(
        sellerData.user_id,
        sellerData.idr,
        chosenDate
      );
      setData(response.data.data);
    } catch (error) {
      console.log("loadProfitMargin error", error);
    }
  };

  const handleMonthChange = (e) => {
    setSelectedMonth(parseInt(e.target.value));
  };

  const currentMonthLabel = monthOptions[selectedMonth]?.label || "";

  return (
    <ProfitMarginContainer>
      <ProfitMarginHeader>
        <ProfitMarginTitle>
          <FormattedMessage
            id="app.profit_margin_title"
            defaultMessage="Margen de ganancia en"
          />
          {` ${currentMonthLabel}`}
        </ProfitMarginTitle>
        <MonthSelect value={selectedMonth} onChange={handleMonthChange}>
          {monthOptions.map((month) => (
            <option key={month.value} value={month.value}>
              {month.label}
            </option>
          ))}
        </MonthSelect>
      </ProfitMarginHeader>
      <MetricsRow>
        <MetricCard bgColor="rgba(94, 148, 248, 0.08)">
          <MetricLabel>
            <FormattedMessage
              id="app.profit_margin_revenue"
              defaultMessage="Ingresos totales"
            />
          </MetricLabel>
          <MetricValue color="#073d72">
            <Money value={data?.total_revenue || 0} decimalScale={0} />
          </MetricValue>
        </MetricCard>
        <MetricCard bgColor="rgba(174, 174, 174, 0.08)">
          <MetricLabel>
            <FormattedMessage
              id="app.profit_margin_cost"
              defaultMessage="Costo total"
            />
          </MetricLabel>
          <MetricValue>
            <Money value={data?.total_cost || 0} decimalScale={0} />
          </MetricValue>
        </MetricCard>
        <MetricCard bgColor="rgba(10, 205, 129, 0.08)">
          <MetricLabel>
            <FormattedMessage
              id="app.profit_margin_profit"
              defaultMessage="Ganancia"
            />
          </MetricLabel>
          <MetricValue color="#0ACD81">
            <Money value={data?.total_profit || 0} decimalScale={0} />
          </MetricValue>
        </MetricCard>
        <MetricCard bgColor="rgba(10, 205, 129, 0.12)">
          <MetricLabel>
            <FormattedMessage
              id="app.profit_margin_percentage"
              defaultMessage="Margen %"
            />
          </MetricLabel>
          <MarginPercentage value={data?.profit_margin_percentage || 0}>
            {(data?.profit_margin_percentage || 0).toFixed(2)}%
          </MarginPercentage>
        </MetricCard>
      </MetricsRow>
    </ProfitMarginContainer>
  );
};
